import i18n from 'helpers/i18n';
import messages from './messages';
import { useState } from 'react';
import { Accordion } from '@radix-ui/react-accordion';
import { DimensionSection } from './DimensionSection';
import { RejectVideoButton } from './RejectVideoButton';
import { toastError } from 'components/Organization/Toast';
import { Text, Button, Dialog } from '@teachstonellc/component-library';
import PageLoader from 'components/PageLoader';

import {
  useGetDoubleCoderScores,
  GetDoubleCoderScoresResponse,
  completeDoubleCoderScores
} from 'actions/video-obs/video-uploads';

interface ScoresInputViewProps {
  /** The ID of the video upload, used for submitting scores or rejecting the video. */
  videoUploadId: number;

  /** The scores data for the video, including dimensions and indicators. */
  domains: GetDoubleCoderScoresResponse;

  /** Callback function invoked after the scores have been successfully submitted. */
  onScoresSubmitted: () => void;

  /** Callback function invoked when the video is rejected. */
  onVideoRejected: () => void;
}

export function ScoresInputView({
  videoUploadId,
  domains,
  onScoresSubmitted,
  onVideoRejected
}: ScoresInputViewProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit() {
    setIsLoading(true);

    completeDoubleCoderScores(videoUploadId)
      .then(onScoresSubmitted, error =>
        toastError({
          message:
            error.response.data?.error || i18n.ft(messages.scores.submitError)
        })
      )
      .finally(() => setIsLoading(false));
  }

  return (
    <div>
      <div className="p-6 bg-ds-white rounded-xl">
        <Text variant="h4" as="div">
          {i18n.ft(messages.scores.title)}
        </Text>

        <div className="mt-6 space-y-4">
          <Accordion type="single" collapsible>
            {domains
              .map(d =>
                d.dimensions.map(dim => (
                  <DimensionSection
                    key={dim.slug}
                    videoUploadId={videoUploadId}
                    dimension={dim}
                  />
                ))
              )
              .flat()}
          </Accordion>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        <Button
          fullWidth
          size="large"
          variant="primary"
          onClick={() => setIsSubmitting(true)}
        >
          {i18n.ft(messages.scores.submitScores)}
        </Button>

        <Dialog
          open={isSubmitting}
          title={i18n.ft(messages.scores.confirmSubmissionTitle)}
          description={i18n.ft(messages.scores.confirmSubmissionDescription)}
          onOpenChange={value => setIsSubmitting(value)}
          actionButton={
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                disabled={isLoading}
                onClick={() => setIsSubmitting(false)}
              >
                {i18n.ft(messages.scores.confirmSubmissionCancelButton)}
              </Button>
              <Button
                variant="primary"
                loading={isLoading}
                onClick={handleSubmit}
              >
                {i18n.ft(messages.scores.confirmSubmissionConfirmButton)}
              </Button>
            </div>
          }
        />

        <RejectVideoButton
          variant="aux"
          videoUploadId={videoUploadId}
          onVideoRejected={onVideoRejected}
        >
          {i18n.ft(messages.scores.returnToQueueButton)}
        </RejectVideoButton>
      </div>
    </div>
  );
}

interface ScoresInputProps {
  /** The ID of the video upload, used for submitting scores or rejecting the video. */
  videoUploadId: number;

  /** Callback function invoked after the scores have been successfully submitted. */
  onScoresSubmitted: () => void;

  /** Callback function invoked when the video is rejected. */
  onVideoRejected: () => void;
}

export function ScoresInput({
  videoUploadId,
  onScoresSubmitted,
  onVideoRejected
}: ScoresInputProps) {
  const { data, isLoading } = useGetDoubleCoderScores(videoUploadId);

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <ScoresInputView
      domains={data!}
      videoUploadId={videoUploadId}
      onVideoRejected={onVideoRejected}
      onScoresSubmitted={onScoresSubmitted}
    />
  );
}
